// =============================================================================
// GENERATED by scripts/build-shared.cjs from functions/src/shared/assetRollup.js
// DO NOT EDIT — edit the canonical file and re-run `node scripts/build-shared.cjs`.
// (TASK-9375 S2: ONE asset stock / per-yard rollup math — the Roster's
// "Group by branch" cards and the organization rollup.)
//
// ES module; roster.js is its only browser importer (Pitfall #1).
// The body below is the canonical file's PORTABLE CORE, copied byte for byte.
// =============================================================================
/** The bucket key for assets tagged with a yard slug that left settings.yards.
 *  Same literal as roster.js ASSETS_FORMER_YARD_KEY (pinned equal by the S2 test). */
const ASSETS_FORMER_YARD_KEY = '__former__';

/** Trimmed part number ('' when none). Same rule as roster.js `_assetPartNo`. PURE. */
function _assetPartNo(a) {
  const p = a && a.partNumber;
  return (p === null || p === undefined) ? '' : String(p).trim();
}

/** TASK-9275 — ONE rule, shared in spirit by every surface that lists an asset
 *  (roster cards + badge, the dispatch pickers, the ticket-review equipment
 *  banner): a quantity-tracked asset whose on-hand count has gone BELOW ZERO.
 *
 *  Deliberately NOT gated on `consumable`, which is what this check used to read.
 *  The deduct writers only touch consumables today (5 of 72 quantity-tracked
 *  assets product-wide, measured 2026-09-22), but the asset edit form will save a
 *  negative on a `standard` asset perfectly happily, and flipping `consumable`
 *  off an already-negative asset used to make the warning vanish while the data
 *  stayed wrong. The flag describes the DATA, not the writer that produced it. */
function assetStockIsNegative(asset) {
  return !!asset && asset.trackingMode === 'quantity' && Number(asset.quantity || 0) < 0;
}

/** TASK-4810 — ONE reading of a quantity-mode RENTAL's stock. PURE. Same rule
 *  as dispatch.js `rentalStockView` (a duplicate pinned equal by the S2 parity
 *  test). `out` counts toward totals only up to `owned` (`outCounted`), so one
 *  item's impossible count (Split Rock's Rubber Mat: 3,765 out of 1,597) can't
 *  bleed into a branch's Out/Available; the excess is `overBy` and the item is
 *  flagged `drift` ("needs recount") instead. Non-rentals -> null. */
function rentalStockView(asset) {
  if (!asset || asset.trackingMode !== 'quantity' || !(asset.quantityType === 'rental' || asset.isRental)) return null;
  const owned = Number(asset.quantity) || 0;
  const out = Math.max(0, Number(asset.deployedQty) || 0);
  return { owned, out, outCounted: Math.min(out, Math.max(0, owned)), available: Math.max(0, owned - out),
    overBy: Math.max(0, out - owned), drift: out > owned };
}

/**
 * Per-branch rollup over the ACTIVE assets (inactive docs never count).
 * PURE — no DOM, no RosterState — so the math is unit-testable in a vm.
 *
 * Returns one bucket per yard in `yards` order (always, even at zero), then an
 * "Unassigned" bucket for `!yardId` and a "Former facility" bucket for slugs
 * that are no longer in `yards`; those two only when they hold something.
 *
 * Bucket numbers:
 *   items      — active docs
 *   total      — Σ quantity (quantity mode) + 1 per individual-mode doc
 *   out        — Σ deployedQty (rental quantity docs) + individual docs deployed
 *   available  — total − out
 *   pctOut     — out / denominator, rounded, CLAMPED to 100 (TASK-4810 drift is
 *                live on Split Rock — never render 235%); null when denominator 0
 *   denominator— Σ quantity of RENTAL quantity docs + individual rentals (a
 *                consumable is never "out", so it never enters the denominator)
 *   drift      — any rental doc has more out than it owns (TASK-4810). That
 *                doc's Out is counted only up to what it owns, so its excess
 *                cannot shrink the bucket's Available; `driftUnits` = the excess
 *   negative   — any doc in the bucket is below zero (card gets the low-stock tint)
 */
function computeAssetBranchRollups(assets, yards) {
  const yardList = Array.isArray(yards) ? yards : [];
  const known = new Set(yardList.map(y => y && y.slug).filter(Boolean));
  const buckets = [];
  const bySlug = new Map();
  const mk = (key, label, extra) => {
    const b = Object.assign({ key, label, yard: null, unassigned: false, former: false,
      items: 0, total: 0, out: 0, available: 0, denominator: 0, pctOut: null, drift: false, driftUnits: 0, negative: false }, extra || {});
    buckets.push(b);
    return b;
  };
  yardList.forEach(y => { if (y && y.slug) bySlug.set(y.slug, mk(y.slug, y.label || y.slug, { yard: y })); });
  const unassigned = mk('', 'Unassigned', { unassigned: true });
  const former = mk(ASSETS_FORMER_YARD_KEY, 'Former facility', { former: true });

  for (const a of (Array.isArray(assets) ? assets : [])) {
    if (!a || a.active === false) continue;
    const slug = a.yardId || '';
    const b = slug === '' ? unassigned : (known.has(slug) ? bySlug.get(slug) : former);
    b.items++;
    const qty = Number(a.quantity) || 0;
    if (a.trackingMode === 'quantity') {
      b.total += qty;
      if (a.quantityType === 'rental') {
        // TASK-4810 — per doc: out capped at owned, excess flagged.
        const rv = rentalStockView(a);
        b.out += rv.outCounted;
        b.denominator += qty;
        if (rv.drift) { b.drift = true; b.driftUnits += rv.overBy; }
      }
      if (assetStockIsNegative(a)) b.negative = true;
    } else {
      // Individual (serialized) asset = one unit. Out when deployed on rental.
      b.total += 1;
      const deployed = a.deploymentStatus === 'deployed';
      if (deployed) b.out += 1;
      if (a.isRental === true || deployed) b.denominator += 1;
    }
  }

  for (const b of buckets) {
    b.available = b.total - b.out;
    b.pctOut = b.denominator > 0 ? Math.min(100, Math.round((b.out / b.denominator) * 100)) : null;
  }
  return buckets.filter(b => b.yard || b.items > 0);
}

/**
 * TASK-9369 — units of a quantity-mode asset that can leave the yard: total
 * minus what is out on rental (a non-rental has nothing out). Floored at 0 so a
 * TASK-4810 drift doc (deployedQty > quantity) reads "0 available", not "-5".
 * PURE.
 */
function _assetTransferableQty(asset) {
  const qty = Number(asset && asset.quantity) || 0;
  const deployed = asset && asset.quantityType === 'rental' ? (Number(asset.deployedQty) || 0) : 0;
  return Math.max(0, qty - deployed);
}

/**
 * THE stock-item identity rule (TASK-9375 S2, spec §7): is `other` the same
 * stock item as `asset`? PURE. With a part number: exact RAW part-number match
 * (no trim / type folding). Without one: only another part-less doc with the
 * same trimmed, case-folded, non-empty name — never "any part-less doc". An
 * asset with neither has no identity and matches nothing. No yard, id or
 * first-match logic here: the org transfer (S4) collects ALL candidates with
 * this so ambiguity is detectable; findYardTransferTarget keeps its own
 * first-match + self-exclusion wrapper.
 */
function assetIsSameStockItem(asset, other) {
  const pn = _assetPartNo(asset);
  const nm = String(asset?.name || '').trim().toLowerCase();
  if (!pn && !nm) return false;
  return !!other && (pn
    ? other.partNumber === asset.partNumber // raw exact match, unchanged (COD: no trim/type folding)
    : (!_assetPartNo(other) && String(other.name || '').trim().toLowerCase() === nm));
}

/**
 * Yard transfer merge target: the doc in the destination yard that IS the same
 * stock item (assetIsSameStockItem), first match, never the source doc itself.
 * PURE.
 */
function findYardTransferTarget(assets, asset, assetId, toYardId) {
  const pn = _assetPartNo(asset);
  const nm = String(asset?.name || '').trim().toLowerCase();
  if (!pn && !nm) return undefined;
  return (assets || []).find(a =>
    a && a.id !== assetId &&
    (a.yardId || '') === toYardId &&
    assetIsSameStockItem(asset, a)
  );
}

export { ASSETS_FORMER_YARD_KEY, assetStockIsNegative, rentalStockView, computeAssetBranchRollups, _assetTransferableQty, findYardTransferTarget, assetIsSameStockItem };
